export default function CFIsReadinessReport() {
  const acsAreas = [
    { area: 'Preflight Preparation', score: 92, status: 'ready', flights: 12 },
    { area: 'Preflight Procedures', score: 88, status: 'ready', flights: 15 },
    { area: 'Airport Operations', score: 85, status: 'ready', flights: 18 },
    { area: 'Takeoffs & Landings', score: 78, status: 'near-ready', flights: 22 },
    { area: 'Performance Maneuvers', score: 82, status: 'ready', flights: 14 },
    { area: 'Ground Reference', score: 75, status: 'near-ready', flights: 10 },
    { area: 'Navigation', score: 68, status: 'needs-work', flights: 8 },
    { area: 'Slow Flight & Stalls', score: 90, status: 'ready', flights: 16 },
    { area: 'Emergency Operations', score: 72, status: 'near-ready', flights: 11 },
    { area: 'Night Operations', score: 65, status: 'needs-work', flights: 6 }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full mb-6">
            <i className="ri-file-list-3-line text-teal-600 text-lg"></i>
            <span className="text-sm font-medium text-teal-700">Readiness Assessment</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Pre-Solo Readiness Reports
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Data-driven assessments aligned with ACS standards. Know exactly when your students are ready for solo flight, checkrides, and other milestones.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-200 p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Sarah Mitchell — Solo Readiness</h3>
                  <p className="text-gray-600">28.5 hours • Last updated 2 days ago</p>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-teal-600 mb-1">82%</div>
                  <div className="text-sm text-gray-600">Overall Score</div>
                </div>
              </div>

              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">Readiness Progress</span>
                  <span className="text-sm text-gray-600">Recommended: 85% for solo</span>
                </div>
                <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-teal-500 to-teal-600 rounded-full" style={{ width: '82%' }}></div>
                </div>
              </div>

              <div className="space-y-3 mb-8">
                {acsAreas.map((area, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900">{area.area}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-600">{area.flights} flights</span>
                          <span className="text-sm font-bold text-gray-900">{area.score}%</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              area.status === 'ready' ? 'bg-green-500' :
                              area.status === 'near-ready' ? 'bg-amber-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${area.score}%` }}
                          ></div>
                        </div>
                        <span className={`text-xs font-medium whitespace-nowrap ${
                          area.status === 'ready' ? 'text-green-600' :
                          area.status === 'near-ready' ? 'text-amber-600' :
                          'text-red-600'
                        }`}>
                          {area.status === 'ready' ? 'Ready' :
                           area.status === 'near-ready' ? 'Near Ready' :
                           'Needs Work'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <button className="flex-1 px-6 py-3 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-download-line mr-2"></i>
                  Export Report
                </button>
                <button className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-share-line mr-2"></i>
                  Share with Student
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-6 border border-green-200">
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-check-double-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">Strengths</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                  <span>Excellent preflight procedures and checklist discipline</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                  <span>Consistent slow flight and stall recovery technique</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                  <span>Strong radio communication skills</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl p-6 border border-amber-200">
              <div className="w-12 h-12 bg-amber-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-alert-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">Focus Areas</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-amber-600 mt-0.5"></i>
                  <span>Navigation planning and pilotage skills need more practice</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-amber-600 mt-0.5"></i>
                  <span>Night operations require additional training flights</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-amber-600 mt-0.5"></i>
                  <span>Base-to-final turn consistency</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-6 border border-teal-200">
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-lightbulb-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">Recommendation</h4>
              <p className="text-sm text-gray-700 mb-4">
                Sarah is nearly ready for solo flight. Recommend 2-3 more pattern sessions focusing on base-to-final turns, then conduct a pre-solo stage check.
              </p>
              <div className="flex items-center gap-2 text-sm text-teal-700 font-medium">
                <i className="ri-calendar-line"></i>
                <span>Estimated solo date: Jan 22-25, 2025</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-6 border border-teal-100">
            <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-file-chart-line text-white text-2xl"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">ACS-Aligned Scoring</h4>
            <p className="text-gray-600 text-sm">
              Every assessment is mapped to FAA Airman Certification Standards, ensuring your students meet all requirements before solo or checkride.
            </p>
          </div>

          <div className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-6 border border-teal-100">
            <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-bar-chart-box-line text-white text-2xl"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">Data-Driven Decisions</h4>
            <p className="text-gray-600 text-sm">
              Remove guesswork from milestone decisions. Readiness scores are calculated from actual flight performance data, not subjective impressions.
            </p>
          </div>

          <div className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-6 border border-teal-100">
            <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-parent-line text-white text-2xl"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">Student & Parent Sharing</h4>
            <p className="text-gray-600 text-sm">
              Share progress reports with students and parents to build confidence and demonstrate clear advancement toward training goals.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}